import {
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Table
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { apiBasePath, REFRESH_INTERVAL, REFRESH_INTERVAL_OPTIONS } from '../../../utils/constants';
import dayjs from 'dayjs';
import { ButtonExportData, CustomDatePicker } from '../../common';
import axios from 'axios';
import locale from 'antd/es/date-picker/locale/es_ES';
import { normalizeInvoiceData, normalizeInvoiceDataForExport } from './normalize';
import { ActionColumn, MotivoCancelColumn } from '../../common/tableCustomColumns';
import { useThrottledAsync } from '../../../hooks/useThrottledAsync';
import '../invoice/invoiceMonitor.css';
import fileDownload from 'js-file-download';

export const InvoiceManagement = () => {
  const today = dayjs();
  const isFirstWeek = today.date() <= 5;

  const defaultValuesDates = {
    fromDate: isFirstWeek ? dayjs().subtract(1, 'month').startOf('month') : dayjs().startOf('month'),
    toDate: today,
    client: 0,
    oper_id: '',
    parking: 0,
    status: 0,
    invoice_type: 0
  };

  const [loader, setLoader] = useState(false);
  const [invoiceList, setInvoiceList] = useState([]);
  const [parkingList, setParkingList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [dataFilter, setDataFilter] = useState(defaultValuesDates);
  const [exportData, setExportData] = useState([]);
  const [refreshInterval, setRefreshInterval] = useState(REFRESH_INTERVAL);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [loaderDownload, setLoaderDownload] = useState({});

  const getFiltersData = async () => {
    setLoader(true);
    try {
      const responseParkingList = await axios.get(`${apiBasePath}/reference-data/parking`);
      const responseClientList = await axios.get(`${apiBasePath}/customer`);

      setParkingList(responseParkingList.data.lstParking);
      setClientList(responseClientList.data.lstCustomer);
    } catch (e) {
      console.error('🚀 ~ getFiltersData ~ e:', e);
    } finally {
      setLoader(false);
    }
  };

  const getListInvoice = async () => {
    setLoader(true);
    try {
      const responseInvoiceList = await axios.get(
        `${apiBasePath}/invoice/cancel-operation-monitor?clie_id=${dataFilter.client}&parking_id=${dataFilter.parking}&from_oper_fecha=${dataFilter.fromDate.format('YYYY-MM-DD')}&origin=0&status=${dataFilter.status}&to_oper_fecha=${dataFilter.toDate.format('YYYY-MM-DD')}&voucher=${dataFilter.oper_id}&invoice_type=${dataFilter.invoice_type}`
      );
      const dataInvoices = responseInvoiceList.data.lstFacturasCanceladas;
      const { dataSource, columns } = normalizeInvoiceData(dataInvoices);
      const exportData = normalizeInvoiceDataForExport(dataInvoices);
      setExportData(exportData);
      setInvoiceList(dataSource);
      setColumns(columns);
    } catch (e) {
      console.error('🚀 ~ getListInvoice ~ e:', e);
    } finally {
      setLoader(false);
    }
  };

  const [handleRefresh, isRefreshing] = useThrottledAsync(async () => {
    await getListInvoice();
  }, 3000);

  useEffect(() => {
    getFiltersData();
    getListInvoice();
  }, [dataFilter.toDate]);

  useEffect(() => {
    if (refreshInterval > 0) {
      const intervalId = setInterval(() => {
        handleRefresh();
      }, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [refreshInterval, handleRefresh]);


  const downLoadFile = async (record, xtencion) => {
    setLoaderDownload({ ...loaderDownload, [record.oper_id]: true, [record.oper_id + xtencion]: true });
    try {
      const idOper = record.oper_id;
      const response = await fetch(`${apiBasePath}/invoice/download-file/oper_id/${idOper}/discriminator/${xtencion}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const blob = await response.blob();
      const filename = `Factura_${idOper}.${xtencion}`;

      fileDownload(blob, filename);
    } catch (error) {
      console.error('Error downloading the PDF:', error);
    } finally {
      setLoaderDownload({ ...loaderDownload, [record.oper_id]: false, [record.oper_id + xtencion]: false });
    }
  };

  const columnsTable = useMemo(
    () => [
      ...(columns ?? []),
      MotivoCancelColumn({ invoiceList, filteredValue: filteredInfo.motivo_descripcion || null }),
      ActionColumn({ handleCustomAction: downLoadFile, loader: loaderDownload }),
    ],
    [columns, filteredInfo, ActionColumn, invoiceList]
  );

  const handleChange = (filters) => {
    setFilteredInfo(filters);
  };

  return (
    <div className="basic-layout">
      <Flex justify="start" align="center" className="basic-layout-header">
        <h1 style={{ margin: '0.5rem 0' }}>Gestión de Facturas</h1>
      </Flex>
      <Row gutter={16} className="filters-container">
        <Col md={4} sm={6}>
          <Form.Item name="client" label="Cliente" layout="vertical">
            <Select
              loading={loader}
              allowClear
              showSearch
              defaultValue={0}
              onChange={(value) => setDataFilter({ ...dataFilter, client: value })}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}>
              <Select.Option key={'all_clients'} value={0} label={'todos'}>
                {'Todos'}
              </Select.Option>
              {clientList?.map((customer) => (
                <Select.Option
                  key={customer.clie_id}
                  value={customer.clie_id}
                  label={`${customer.clie_rfc} - ${customer.clie_nombre} ${customer.clie_ap} ${customer.clie_am}`}>
                  {`${customer.clie_rfc} - ${customer.clie_nombre} ${customer.clie_ap} ${customer.clie_am}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col md={4} sm={6}>
          <Form.Item name="parking" label="Estacionamiento" layout="vertical">
            <Select
              loading={loader}
              allowClear
              showSearch
              defaultValue={0}
              onChange={(value) => setDataFilter({ ...dataFilter, parking: value })}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}>
              <Select.Option key={'all_parking'} value={0} label={'todos'}>
                {'Todos'}
              </Select.Option>
              {parkingList?.map((parking) => (
                <Select.Option
                  key={parking.cates_id_int}
                  value={parking.cates_id_int}
                  label={`${parking.cates_id_int} - ${parking.cates_descripcion_str}`}>
                  {`${parking.cates_id_int} - ${parking.cates_descripcion_str}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col md={2} sm={4}>
          <Form.Item name="status" label="Estatus" layout="vertical">
            <Select
              allowClear
              defaultValue={0}
              onChange={(value) => setDataFilter({ ...dataFilter, status: value })}
              options={[
                { value: 0, label: 'Todas' },
                { value: 1, label: 'Activas' },
                { value: 2, label: 'Canceladas' }
              ]}
            />
          </Form.Item>
        </Col>
        <Col md={2} sm={4}>
          <Form.Item name="invoice_type" label="Tipo" layout="vertical">
            <Select
              allowClear
              defaultValue={0}
              onChange={(value) => setDataFilter({ ...dataFilter, invoice_type: value })}
              options={[
                { value: 0, label: 'Todas' },
                { value: 1, label: 'Boletaje' },
                { value: 2, label: 'Pension' }
              ]}
            />
          </Form.Item>
        </Col>
        <Col md={4} sm={12}>
          <Form.Item name="oper_id" label="Número de Operación" layout="vertical">
            <Input
              value={dataFilter.oper_id}
              onChange={(e) => {
                setDataFilter({ ...dataFilter, oper_id: e.target.value });
              }}
            />
          </Form.Item>
        </Col>
        <Col md={6} sm={12} align="right">
          <Flex gap="small" justify="flex-end">
            <Form.Item name="dateFromTo" label="Fechas" layout="vertical">
              <CustomDatePicker
                picker="week"
                locale={locale}
                defaultValue={[dataFilter.fromDate, dataFilter.toDate]}
                format="DD-MM-YYYY"
                onChange={(value) => {
                  if (value) {
                    setDataFilter({ ...dataFilter, fromDate: value[0], toDate: value[1] });
                  }
                }}
                onRefresh={handleRefresh}
                buttonText="Actualizar"
                loading={loader || isRefreshing}
              />
            </Form.Item>
            <Form.Item name="refresh_interval" label="Refrescar" layout="vertical" style={{ minWidth: '80px' }}>
              <Select
                defaultValue={REFRESH_INTERVAL}
                onChange={(value) => setRefreshInterval(value)}
                options={REFRESH_INTERVAL_OPTIONS}
              />
            </Form.Item>
          </Flex>
        </Col>
      </Row>
      <Divider style={{ margin: '0.5rem 0' }} />
      <Flex justify="end" style={{ marginBottom: '1rem' }}>
        <ButtonExportData jsonData={exportData} fileName="Facturas_Gestion" />
      </Flex>
      <Table
        size="small"
        id="invoice-monitor-table"
        dataSource={invoiceList}
        columns={columnsTable}
        loading={loader}
        onChange={handleChange}
        scrollToFirstRowOnChange={true}
        scroll={{ y: window.innerHeight - 450 }}
      />
    </div>
  );
};
